import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import ArticleCard from '../components/ArticleCard';
import HilltopBanner from '../components/HilltopBanner';

const SearchPage = () => {
  const [results, setResults] = useState({ articles: [], calculators: [] });
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q');

  useEffect(() => {
    const performSearch = async () => {
      if (!query) return;
      setLoading(true);
      try {
        const [articlesRes, calculatorsRes] = await Promise.all([
          apiService.getArticles(),
          apiService.getCalculators()
        ]);

        const searchTerm = query.toLowerCase();
        const filteredArticles = articlesRes.data?.filter(article =>
          article.title.toLowerCase().includes(searchTerm) ||
          article.meta_description.toLowerCase().includes(searchTerm)
        ) || [];

        const filteredCalculators = calculatorsRes.data?.filter(calc =>
          calc.name.toLowerCase().includes(searchTerm) ||
          calc.description.toLowerCase().includes(searchTerm)
        ) || [];

        setResults({ articles: filteredArticles, calculators: filteredCalculators });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };
    performSearch();
  }, [query]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto"></div>
          <p className="font-label-caps text-xs text-outline tracking-widest uppercase">Scanning_Registry...</p>
        </div>
      </div>
    );
  }

  const totalResults = results.articles.length + results.calculators.length;

  return (
    <div className="min-h-screen py-20 px-6 md:px-12">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-20 space-y-4">
          <span className="label-caps text-brand">Query_Results</span>
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1]">
            SEARCH_OUTPUT
          </h1>
          <p className="text-slate-400 text-lg">
            Query: <span className="text-brand font-medium">"{query}"</span>
            {' — '}
            <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">
              {totalResults} RESULT{totalResults !== 1 ? 'S' : ''} FOUND
            </span>
          </p>
        </div>

        {/* Articles Results */}
        {results.articles.length > 0 && (
          <section className="mb-20">
            <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-4">
              <span className="text-[10px] font-bold text-brand uppercase tracking-widest">Intel_Nodes</span>
              <h2 className="text-2xl font-bold text-white">ARTICLES</h2>
              <span className="px-3 py-0.5 rounded-full bg-brand/10 text-brand font-bold text-[10px] border border-brand/30">
                {results.articles.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.articles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>
        )}

        {/* Calculators Results */}
        {results.calculators.length > 0 && (
          <section className="mb-20">
            <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-4">
              <span className="text-[10px] font-bold text-brand uppercase tracking-widest">Compute_Engines</span>
              <h2 className="text-2xl font-bold text-white">CALCULATORS</h2>
              <span className="px-3 py-0.5 rounded-full bg-brand/10 text-brand font-bold text-[10px] border border-brand/30">
                {results.calculators.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.calculators.map(calculator => (
                <Link
                  key={calculator.id}
                  to={`/calculators/${calculator.slug}`}
                  className="glass group flex flex-col items-start text-left p-8 rounded-3xl hover:scale-[1.01] transition-all duration-300 focus-glow"
                >
                  <div className="w-12 h-12 glass rounded-xl flex items-center justify-center mb-6 group-hover:bg-brand/20 transition-colors border border-brand/20">
                    <span className="material-symbols-outlined text-brand group-hover:scale-110 transition-transform">calculate</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-brand transition-colors leading-tight">
                    {calculator.name}
                  </h3>
                  <p className="text-slate-400 line-clamp-2 flex-grow mb-6 text-sm">
                    {calculator.description}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 group-hover:text-brand transition-colors tracking-widest uppercase">
                    EXECUTE_MODULE
                    <span className="material-symbols-outlined text-xs group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Ad Section */}
        <div className="flex justify-center py-12 border-y border-white/5 mb-20">
          <HilltopBanner />
        </div>

        {/* No Results */}
        {totalResults === 0 && query && (
          <div className="text-center py-32 glass rounded-2xl">
            <span className="material-symbols-outlined text-6xl text-slate-600 mb-6">search_off</span>
            <h2 className="text-2xl font-bold text-brand mb-4 uppercase">Zero_Results</h2>
            <p className="text-slate-400 mb-2 text-lg">
              No registry entries found for <span className="text-brand">"{query}"</span>
            </p>
            <p className="text-[10px] font-bold text-slate-600 tracking-widest mb-12 uppercase">
              SUGGESTION: BROADEN_QUERY_PARAMETERS
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/articles" className="bg-brand text-[#050805] px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform glow-green">
                BROWSE_ARTICLES
              </Link>
              <Link to="/calculators" className="bg-transparent text-brand border border-brand/30 px-8 py-3 rounded-full font-bold hover:bg-brand/5 transition-colors">
                BROWSE_ENGINES
              </Link>
            </div>
          </div>
        )}

        {/* No Query */}
        {!query && (
          <div className="text-center py-32 glass rounded-2xl">
            <span className="material-symbols-outlined text-6xl text-slate-600 mb-6">manage_search</span>
            <h2 className="text-2xl font-bold text-brand mb-4 uppercase">Enter_Query</h2>
            <p className="text-slate-400 text-lg">Use the search field above to scan the registry.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;